import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"

import Badge from "@kit/design/badge"
import { Checkbox } from "@kit/design/checkbox"
import { Field } from "@kit/design/field"
import Icon from "@kit/design/icon"
import Modal, { Action } from "@kit/design/modal"
import Stack from "@kit/design/stack"
import { toast } from "@kit/design/toast"
import { useTelemetry } from "@kit/design/event-emitter"

import { analytics } from "utils/analytics"
import { optIntoSignals } from "sdk/signals"
import { useCurrentUser } from "hooks/use-current-user"
import { useStaticFeature } from "hooks/use-feature"
import { discountTrackingProperties } from "app/shared/components/billing/discount-tracking"
import {
  LEARN_MORE_URL,
  SIGNALS_STATE,
  SIGNALS_TELEMETRY_FEATURE
} from "pages/signals/constants"
import { SIGNALS_TERMS, CURRENT_SIGNALS_TERMS_VERSION } from "./signals-terms"
import { SignalsWhatToExpectStepper } from "./signals-what-to-expect-stepper"

const END_TRIAL_EARLY_ACKNOWLEDGEMENT =
  "I understand that turning on Signals will end my Pro trial and my credit card will be charged today."

export const SignalsOptInModal = ({
  onOptInSuccess,
  onSkip,
  onClose,
  upgradedPlan
}) => {
  const { signalsAccessLevel } = useCurrentUser()
  const endTrialEarly = useStaticFeature("signals_end_trial_early")
  const [isPending, setIsPending] = useState(false)
  const [hasAcknowledgedCharge, setHasAcknowledgedCharge] = useState(false)
  const telemetry = useTelemetry({ feature: SIGNALS_TELEMETRY_FEATURE })
  const modalLabel = "Turn on Subscriber Signals?"

  const requiresChargeAcknowledgement =
    endTrialEarly.enabled &&
    signalsAccessLevel?.state === SIGNALS_STATE.PRO_TRIAL

  const isPostUpgrade = Boolean(upgradedPlan)
  const discountTracking = isPostUpgrade
    ? discountTrackingProperties(upgradedPlan)
    : {}

  useEffect(() => {
    analytics.track("Element", "Opened", {
      ...telemetry,
      element_type: "modal",
      name: "signals_opt_in_modal",
      label: modalLabel,
      is_post_upgrade: isPostUpgrade,
      is_end_trial_early: requiresChargeAcknowledgement,
      signals_access_level: signalsAccessLevel?.state,
      ...discountTracking
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    if (!isPending) {
      analytics.track("Element", "Closed", {
        ...telemetry,
        element_type: "modal",
        name: "signals_opt_in_modal",
        is_post_upgrade: isPostUpgrade,
        is_end_trial_early: requiresChargeAcknowledgement,
        signals_access_level: signalsAccessLevel?.state,
        ...discountTracking
      })
    }

    onClose()
  }

  const handleSkip = () => {
    analytics.track("Element", "Clicked", {
      ...telemetry,
      element_type: "button",
      name: "signals_opt_in_no",
      text: "Not now",
      is_post_upgrade: isPostUpgrade,
      is_end_trial_early: requiresChargeAcknowledgement,
      signals_access_level: signalsAccessLevel?.state,
      ...discountTracking
    })
    onSkip()
  }

  const handleOptIn = async () => {
    analytics.track("Element", "Clicked", {
      ...telemetry,
      element_type: "button",
      name: "signals_opt_in_yes",
      text: "Turn on Signals",
      is_post_upgrade: isPostUpgrade,
      is_end_trial_early: requiresChargeAcknowledgement,
      signals_access_level: signalsAccessLevel?.state,
      ...discountTracking
    })
    setIsPending(true)

    try {
      await optIntoSignals(CURRENT_SIGNALS_TERMS_VERSION)
      onOptInSuccess()
    } catch (error) {
      const serverMessage = error.response?.data?.errors?.[0]

      if (
        !serverMessage &&
        typeof window !== "undefined" &&
        window.SentryClient
      ) {
        window.SentryClient.captureException(error)
      }
      toast({
        type: "error",
        text:
          serverMessage ??
          "Sorry, we couldn't turn on Subscriber Signals. If this error persists, please contact help@kit.com for support."
      })
      setIsPending(false)
    }
  }

  return (
    <Modal
      label={modalLabel}
      onClose={handleClose}
      width="600px"
      data-testid="signals-opt-in-modal"
      telemetry={false}
    >
      <Stack spacing={8} className="items-start">
        <div className="flex flex-col gap-2">
          <Badge color="darkBlue" className="self-start">
            <Icon name="diamond" size="xs" data-icon="inline-start" />
            New Pro feature
          </Badge>
          <p className="text-gray-900 text-sm">
            Subscriber Signals shows your subscribers&apos; demographic data and
            social influence to unlock more business opportunities.{" "}
            <a
              className="underline whitespace-nowrap"
              href={LEARN_MORE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Learn more
              <Icon name="arrow-up-right" className="inline ml-1" />
            </a>
          </p>
        </div>

        <SignalsWhatToExpectStepper />

        <div className="flex flex-col gap-3">
          <p className="text-gray-700 text-xs">
            {SIGNALS_TERMS[CURRENT_SIGNALS_TERMS_VERSION]}
          </p>

          {requiresChargeAcknowledgement && (
            <Field orientation="horizontal">
              <Checkbox
                checked={hasAcknowledgedCharge}
                onCheckedChange={setHasAcknowledgedCharge}
              />
              <Field.Label>{END_TRIAL_EARLY_ACKNOWLEDGEMENT}</Field.Label>
            </Field>
          )}
        </div>
      </Stack>

      <Action
        variant="secondary"
        onClick={handleSkip}
        disabled={isPending}
        telemetry={false}
      >
        Not now
      </Action>
      <Action
        onClick={handleOptIn}
        telemetry={false}
        disabled={
          isPending || (requiresChargeAcknowledgement && !hasAcknowledgedCharge)
        }
        loading={isPending}
      >
        Turn on Signals
      </Action>
    </Modal>
  )
}

SignalsOptInModal.propTypes = {
  onOptInSuccess: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // The plan just upgraded to. Passing it is what marks the opt-in as
  // post-upgrade, so the two can't disagree.
  upgradedPlan: PropTypes.object
}
